import "./TenantManager.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL || "http://127.0.0.1:5000";

const PAYMENT_RANGES = [
  { value: "1-10", label: "1-10" },
  { value: "10-20", label: "10-20" },
  { value: "20-30", label: "20-30" },
];

const emptyParking = () => ({
  numero_parcheggio: "",
  affitto_parcheggio: 0,
  spese_parcheggio: 0,
});

const emptyTenantForm = () => ({
  name: "",
  phone: "",
  apartment_id: "",
  payment_range: "1-10",
  start_date: "",
  end_date: "",
  is_active: true,
  parkings: [],
});

export default function TenantManager() {
  const navigate = useNavigate();

  const [tenants, setTenants] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [visiblePhones, setVisiblePhones] = useState({});

  const [form, setForm] = useState(emptyTenantForm());
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(emptyTenantForm());

  const togglePhone = (tenantId) => {
    setVisiblePhones((prev) => ({
      ...prev,
      [tenantId]: !prev[tenantId],
    }));
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(""), 2500);
  };

  const maskPhone = (phone) => {
    if (!phone) return "-";

    const p = String(phone).trim();

    if (p.length < 6) return "📞 ***";

    const match = p.match(/^\+\d{2,3}/);
    const prefix = match ? match[0] : p.slice(0, 3);
    const last3 = p.slice(-3);

    return `📞 ${prefix} *** *** ${last3}`;
  };

  const cleanNumber = (value) =>
    value === "" || value === null ? 0 : Number(value);

  const normalizeParkings = (parkings) => {
    if (!Array.isArray(parkings)) return [];

    return parkings.map((parking) => ({
      numero_parcheggio: String(parking?.numero_parcheggio || ""),
      affitto_parcheggio: cleanNumber(
        parking?.affitto_parcheggio ?? 0
      ),
      spese_parcheggio: cleanNumber(
        parking?.spese_parcheggio ?? 0
      ),
    }));
  };

  const sortTenantsByApartment = (items) =>
    [...items].sort((a, b) =>
      String(a.apartment_name || "").localeCompare(
        String(b.apartment_name || ""),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        }
      )
    );

  const getAuthHeaders = () => {
    const token = localStorage.getItem("access_token");

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

  const handleUnauthorized = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    localStorage.removeItem("role");

    navigate("/login");
  };

  useEffect(() => {
    fetchTenants();
    fetchApartments();
  }, []);

  const fetchTenants = async () => {
    setError("");

    try {
      const response = await fetch(`${API_URL}/tenants/?status=all`, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401 || response.status === 422) {
        handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        setError(
          data?.error || "Gabim gjatë ngarkimit të qiramarrësve."
        );
        return;
      }

      setTenants(
        sortTenantsByApartment(Array.isArray(data) ? data : [])
      );
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const fetchApartments = async () => {
    setError("");

    try {
      const response = await fetch(`${API_URL}/apartments/`, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401 || response.status === 422) {
        handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        setError(
          data?.error || "Gabim gjatë ngarkimit të apartamenteve."
        );
        return;
      }

      setApartments(Array.isArray(data) ? data : []);
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const addParkingTo = (state, setState) => {
    setState({
      ...state,
      parkings: [
        ...(state.parkings || []),
        emptyParking(),
      ],
    });
  };

  const removeParkingFrom = (index, state, setState) => {
    setState({
      ...state,
      parkings: (state.parkings || []).filter(
        (_, parkingIndex) => parkingIndex !== index
      ),
    });
  };

  const updateParkingField = (
    index,
    field,
    value,
    state,
    setState
  ) => {
    const nextParkings = [...(state.parkings || [])];

    nextParkings[index] = {
      ...nextParkings[index],
      [field]: value,
    };

    setState({
      ...state,
      parkings: nextParkings,
    });
  };

  const buildPayload = (state) => {
    const parkings = normalizeParkings(state.parkings).filter(
      (parking) => {
        const hasNumber =
          (parking.numero_parcheggio || "").trim() !== "";

        const hasCosts =
          Number(parking.affitto_parcheggio || 0) !== 0 ||
          Number(parking.spese_parcheggio || 0) !== 0;

        return hasNumber || hasCosts;
      }
    );

    return {
      name: (state.name || "").trim(),
      phone: (state.phone || "").trim(),
      apartment_id: state.apartment_id || null,
      payment_range: state.payment_range || "1-10",
      start_date: state.start_date || null,
      end_date: state.end_date || null,
      is_active: Boolean(state.is_active),
      parkings,
    };
  };

  const saveTenant = async () => {
    setError("");

    const hasParking = normalizeParkings(form.parkings).some(
      (parking) => (parking.numero_parcheggio || "").trim() !== ""
    );

    if (!form.name || (!form.apartment_id && !hasParking)) {
      setError("Plotëso emrin dhe zgjidh një apartament ose shto të paktën një parking.");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/tenants/`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(buildPayload(form)),
      });

      if (response.status === 401 || response.status === 422) {
        handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        setError(
          data?.error || "Gabim gjatë shtimit të qiramarrësit."
        );
        return;
      }

      setForm(emptyTenantForm());
      showToast("Qiramarrësi u shtua me sukses.");
      fetchTenants();
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const startEdit = (tenant) => {
    setEditing(tenant.id);

    setEditForm({
      name: tenant.name || "",
      phone: tenant.phone || "",
      apartment_id: tenant.apartment_id || "",
      payment_range: tenant.payment_range || "1-10",
      start_date: tenant.start_date || "",
      end_date: tenant.end_date || "",
      is_active: tenant.is_active !== false,
      parkings: normalizeParkings(tenant.parkings || []),
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditForm(emptyTenantForm());
  };

  const updateTenant = async (tenantId) => {
    setError("");

    const hasParking = normalizeParkings(editForm.parkings).some(
      (parking) => (parking.numero_parcheggio || "").trim() !== ""
    );

    if (!editForm.name || (!editForm.apartment_id && !hasParking)) {
      setError("Plotëso emrin dhe zgjidh një apartament ose shto të paktën një parking.");
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/tenants/${tenantId}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify(buildPayload(editForm)),
        }
      );

      if (response.status === 401 || response.status === 422) {
        handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        setError(
          data?.error ||
            "Gabim gjatë përditësimit të qiramarrësit."
        );
        return;
      }

      setEditing(null);
      showToast("Qiramarrësi u përditësua me sukses.");
      fetchTenants();
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const renewTenantContract = async (tenant) => {
    const currentEndDate = tenant.end_date || "";
    const newEndDate = window.prompt(
      "Vendos datën e re të mbarimit të kontratës (YYYY-MM-DD):",
      currentEndDate
    );

    if (newEndDate === null) return;

    const normalizedDate = newEndDate.trim();

    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) {
      setError(
        "Data nuk është e vlefshme. Përdor formatin YYYY-MM-DD."
      );
      return;
    }

    setError("");

    try {
      const response = await fetch(
        `${API_URL}/tenants/${tenant.id}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            name: tenant.name,
            phone: tenant.phone || "",
            apartment_id: tenant.apartment_id,
            payment_range: tenant.payment_range || "1-10",
            start_date: tenant.start_date || null,
            end_date: normalizedDate,
            is_active: tenant.is_active !== false,
            parkings: normalizeParkings(tenant.parkings || []),
          }),
        }
      );

      if (response.status === 401 || response.status === 422) {
        handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        setError(
          data?.error || "Gabim gjatë rinovimit të kontratës."
        );
        return;
      }

      showToast("Kontrata u rinovua me sukses.");
      fetchTenants();
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const changeTenantStatus = async (tenant, activate) => {
    setError("");

    const message = activate
      ? "Dëshiron ta riaktivizosh këtë qiramarrës?"
      : "Dëshiron ta çaktivizosh këtë qiramarrës? Historiku i pagesave do të ruhet.";

    if (!window.confirm(message)) return;

    try {
      const response = await fetch(
        `${API_URL}/tenants/${tenant.id}/${
          activate ? "activate" : "deactivate"
        }`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify(
            activate
              ? {}
              : {
                  end_date:
                    tenant.end_date ||
                    new Date().toISOString().slice(0, 10),
                }
          ),
        }
      );

      if (response.status === 401 || response.status === 422) {
        handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        setError(
          data?.error || "Gabim gjatë ndryshimit të statusit."
        );
        return;
      }

      showToast(
        activate
          ? "Qiramarrësi u riaktivizua."
          : "Qiramarrësi u çaktivizua."
      );

      fetchTenants();
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const deleteTenant = async (tenantId) => {
    setError("");

    if (!window.confirm("Je i sigurt që do ta fshish?")) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/tenants/${tenantId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );

      if (response.status === 401 || response.status === 422) {
        handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        setError(
          data?.error || "Gabim gjatë fshirjes së qiramarrësit."
        );
        return;
      }

      showToast("Qiramarrësi u fshi me sukses.");
      fetchTenants();
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const activeTenants = tenants.filter(
    (tenant) => tenant.is_active !== false
  );

  const inactiveTenants = tenants.filter(
    (tenant) => tenant.is_active === false
  );

  return (
    <div className="tenant-container">
      <h2 className="tenant-title">Qiramarrësit</h2>

      {toast && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            background: "#16a34a",
            color: "#fff",
            padding: "12px 18px",
            borderRadius: "12px",
            boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
            fontWeight: "600",
            zIndex: 9999,
            minWidth: "240px",
          }}
        >
          ✅ {toast}
        </div>
      )}

      {error && (
        <div
          style={{
            marginBottom: "15px",
            color: "#b00020",
            fontWeight: "600",
          }}
        >
          {error}
        </div>
      )}

      <div className="tenant-form">
        <input
          placeholder="Emri"
          value={form.name}
          onChange={(event) =>
            setForm({
              ...form,
              name: event.target.value,
            })
          }
        />

        <input
          placeholder="Telefoni (+355...)"
          value={form.phone}
          onChange={(event) =>
            setForm({
              ...form,
              phone: event.target.value,
            })
          }
        />

        <select
          value={form.apartment_id}
          onChange={(event) =>
            setForm({
              ...form,
              apartment_id: event.target.value,
            })
          }
        >
          <option value="">Pa apartament (vetëm parking)</option>

          {apartments.map((apartment) => (
            <option
              key={apartment.id}
              value={apartment.id}
            >
              {apartment.name}
            </option>
          ))}
        </select>

        <select
          value={form.payment_range}
          onChange={(event) =>
            setForm({
              ...form,
              payment_range: event.target.value,
            })
          }
        >
          {PAYMENT_RANGES.map((range) => (
            <option
              key={range.value}
              value={range.value}
            >
              {range.label}
            </option>
          ))}
        </select>

        <div>
          <div
            style={{
              fontSize: "12px",
              fontWeight: "600",
              marginBottom: "4px",
            }}
          >
            Data e fillimit të kontratës
          </div>

          <input
            type="date"
            title="Data e fillimit të kontratës"
            value={form.start_date}
            onChange={(event) =>
              setForm({
                ...form,
                start_date: event.target.value,
              })
            }
          />
        </div>

        <div>
          <div
            style={{
              fontSize: "12px",
              fontWeight: "600",
              marginBottom: "4px",
            }}
          >
            Data e mbarimit të kontratës
          </div>

          <input
            type="date"
            title="Data e mbarimit të kontratës"
            value={form.end_date}
            onChange={(event) =>
              setForm({
                ...form,
                end_date: event.target.value,
              })
            }
          />
        </div>

        <label className="tenant-checkbox">
          <input
            type="checkbox"
            checked={(form.parkings || []).length > 0}
            onChange={(event) => {
              if (event.target.checked) {
                if ((form.parkings || []).length === 0) {
                  addParkingTo(form, setForm);
                }
              } else {
                setForm({
                  ...form,
                  parkings: [],
                });
              }
            }}
          />
          Ka parking
        </label>

        {(form.parkings || []).map((parking, index) => (
          <div
            key={index}
            className="parking-row"
          >
            <input
              placeholder="Numri parkingut"
              value={parking.numero_parcheggio}
              onChange={(event) =>
                updateParkingField(
                  index,
                  "numero_parcheggio",
                  event.target.value,
                  form,
                  setForm
                )
              }
            />

            <input
              type="number"
              placeholder="Qira parkingu"
              value={parking.affitto_parcheggio}
              onChange={(event) =>
                updateParkingField(
                  index,
                  "affitto_parcheggio",
                  event.target.value,
                  form,
                  setForm
                )
              }
            />

            <input
              type="number"
              placeholder="Shpenzime parkingu"
              value={parking.spese_parcheggio}
              onChange={(event) =>
                updateParkingField(
                  index,
                  "spese_parcheggio",
                  event.target.value,
                  form,
                  setForm
                )
              }
            />

            <button
              type="button"
              className="tenant-btn cancel small"
              onClick={() =>
                removeParkingFrom(
                  index,
                  form,
                  setForm
                )
              }
            >
              Hiq
            </button>
          </div>
        ))}

        {(form.parkings || []).length > 0 && (
          <button
            type="button"
            className="tenant-btn"
            onClick={() => addParkingTo(form, setForm)}
          >
            + Shto parking
          </button>
        )}

        <button
          className="tenant-btn"
          onClick={saveTenant}
        >
          Shto Qiramarrës
        </button>
      </div>

      <h3 style={{ marginTop: 24, marginBottom: 8 }}>
        Qiramarrës aktivë
      </h3>

      <table className="tenant-table">
        <thead>
          <tr>
            <th>Emri</th>
            <th>Telefoni</th>
            <th>Apartamenti</th>
            <th>Parking</th>
            <th>Data pagesës</th>
            <th>Kontrata</th>
            <th>Statusi</th>
            <th>Veprime</th>
          </tr>
        </thead>

        <tbody>
          {activeTenants.map((tenant) => {
            const isEditingRow = editing === tenant.id;

            const parkings = normalizeParkings(
              tenant.parkings || []
            );

            const parkingNumbers = parkings
              .map((parking) =>
                String(
                  parking.numero_parcheggio || ""
                ).trim()
              )
              .filter(Boolean);

            const legacyParkingNumber = String(
              tenant.numero_parcheggio || ""
            ).trim();

            if (
              legacyParkingNumber &&
              !parkingNumbers.includes(
                legacyParkingNumber
              )
            ) {
              parkingNumbers.push(
                legacyParkingNumber
              );
            }

            const parkingSummary =
              parkingNumbers.length > 0
                ? parkingNumbers.join(", ")
                : "Jo";

            return (
              <tr key={tenant.id}>
                <td>
                  {isEditingRow ? (
                    <input
                      value={editForm.name}
                      onChange={(event) =>
                        setEditForm({
                          ...editForm,
                          name: event.target.value,
                        })
                      }
                    />
                  ) : (
                    tenant.name
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <input
                      placeholder="Telefoni (+355...)"
                      value={editForm.phone}
                      onChange={(event) =>
                        setEditForm({
                          ...editForm,
                          phone: event.target.value,
                        })
                      }
                    />
                  ) : (
                    <>
                      {visiblePhones[tenant.id]
                        ? `📞 ${tenant.phone || "-"}`
                        : maskPhone(tenant.phone)}

                      {tenant.phone ? (
                        <button
                          onClick={() =>
                            togglePhone(tenant.id)
                          }
                          title={
                            visiblePhones[tenant.id]
                              ? "Fshih numrin"
                              : "Shfaq numrin"
                          }
                          style={{
                            marginLeft: 10,
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            fontSize: 14,
                          }}
                        >
                          👁
                        </button>
                      ) : null}
                    </>
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <select
                      value={editForm.apartment_id}
                      onChange={(event) =>
                        setEditForm({
                          ...editForm,
                          apartment_id:
                            event.target.value,
                        })
                      }
                    >
                      <option value="">
                        Pa apartament (vetëm parking)
                      </option>

                      {apartments.map((apartment) => (
                        <option
                          key={apartment.id}
                          value={apartment.id}
                        >
                          {apartment.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    tenant.apartment_name || "-"
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <>
                      <label className="tenant-checkbox">
                        <input
                          type="checkbox"
                          checked={
                            (editForm.parkings || [])
                              .length > 0
                          }
                          onChange={(event) => {
                            if (event.target.checked) {
                              if (
                                (editForm.parkings || [])
                                  .length === 0
                              ) {
                                addParkingTo(
                                  editForm,
                                  setEditForm
                                );
                              }
                            } else {
                              setEditForm({
                                ...editForm,
                                parkings: [],
                              });
                            }
                          }}
                        />
                        Po
                      </label>

                      {(editForm.parkings || []).map(
                        (parking, index) => (
                          <div
                            key={index}
                            className="parking-row"
                          >
                            <input
                              placeholder="Numri"
                              value={
                                parking.numero_parcheggio
                              }
                              onChange={(event) =>
                                updateParkingField(
                                  index,
                                  "numero_parcheggio",
                                  event.target.value,
                                  editForm,
                                  setEditForm
                                )
                              }
                            />

                            <input
                              type="number"
                              placeholder="Qira"
                              value={
                                parking.affitto_parcheggio
                              }
                              onChange={(event) =>
                                updateParkingField(
                                  index,
                                  "affitto_parcheggio",
                                  event.target.value,
                                  editForm,
                                  setEditForm
                                )
                              }
                            />

                            <input
                              type="number"
                              placeholder="Shpenzime"
                              value={
                                parking.spese_parcheggio
                              }
                              onChange={(event) =>
                                updateParkingField(
                                  index,
                                  "spese_parcheggio",
                                  event.target.value,
                                  editForm,
                                  setEditForm
                                )
                              }
                            />

                            <button
                              type="button"
                              className="tenant-btn cancel small"
                              onClick={() =>
                                removeParkingFrom(
                                  index,
                                  editForm,
                                  setEditForm
                                )
                              }
                            >
                              Hiq
                            </button>
                          </div>
                        )
                      )}

                      {(editForm.parkings || []).length >
                        0 && (
                        <button
                          type="button"
                          className="tenant-btn"
                          onClick={() =>
                            addParkingTo(
                              editForm,
                              setEditForm
                            )
                          }
                        >
                          + Shto parking
                        </button>
                      )}
                    </>
                  ) : (
                    parkingSummary
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <select
                      value={editForm.payment_range}
                      onChange={(event) =>
                        setEditForm({
                          ...editForm,
                          payment_range:
                            event.target.value,
                        })
                      }
                    >
                      {PAYMENT_RANGES.map((range) => (
                        <option
                          key={range.value}
                          value={range.value}
                        >
                          {range.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    tenant.payment_range || "-"
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        minWidth: "150px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "12px",
                            marginBottom: "4px",
                            fontWeight: "600",
                          }}
                        >
                          Fillimi
                        </div>

                        <input
                          type="date"
                          title="Data e fillimit të kontratës"
                          value={editForm.start_date}
                          onChange={(event) =>
                            setEditForm({
                              ...editForm,
                              start_date:
                                event.target.value,
                            })
                          }
                        />
                      </div>

                      <div>
                        <div
                          style={{
                            fontSize: "12px",
                            marginBottom: "4px",
                            fontWeight: "600",
                          }}
                        >
                          Mbarimi
                        </div>

                        <input
                          type="date"
                          title="Data e mbarimit të kontratës"
                          value={editForm.end_date}
                          onChange={(event) =>
                            setEditForm({
                              ...editForm,
                              end_date:
                                event.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: "13px",
                        lineHeight: "1.6",
                      }}
                    >
                      <div>
                        <strong>Fillimi:</strong>{" "}
                        {tenant.start_date || "-"}
                      </div>

                      <div>
                        <strong>Mbarimi:</strong>{" "}
                        {tenant.end_date || "-"}
                      </div>
                    </div>
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <label className="tenant-checkbox">
                      <input
                        type="checkbox"
                        checked={editForm.is_active}
                        onChange={(event) =>
                          setEditForm({
                            ...editForm,
                            is_active:
                              event.target.checked,
                          })
                        }
                      />
                      Aktiv
                    </label>
                  ) : tenant.is_active !== false ? (
                    "Aktiv"
                  ) : (
                    "Jo aktiv"
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <>
                      <button
                        className="tenant-btn"
                        onClick={() =>
                          updateTenant(tenant.id)
                        }
                      >
                        Ruaj
                      </button>

                      <button
                        className="tenant-btn cancel"
                        onClick={cancelEdit}
                      >
                        Anulo
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="edit-btn"
                        onClick={() =>
                          startEdit(tenant)
                        }
                      >
                        Modifiko
                      </button>

                      <button
                        className="tenant-btn"
                        onClick={() =>
                          renewTenantContract(tenant)
                        }
                      >
                        Rinovo kontratën
                      </button>

                      {tenant.is_active !== false ? (
                        <button
                          className="delete-btn"
                          onClick={() =>
                            changeTenantStatus(
                              tenant,
                              false
                            )
                          }
                        >
                          Çaktivizo
                        </button>
                      ) : (
                        <button
                          className="tenant-btn"
                          onClick={() =>
                            changeTenantStatus(
                              tenant,
                              true
                            )
                          }
                        >
                          Riaktivizo
                        </button>
                      )}

                      <button
                        className="delete-btn"
                        onClick={() =>
                          deleteTenant(tenant.id)
                        }
                      >
                        Fshi
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h3 style={{ marginTop: 36, marginBottom: 8 }}>
        Qiramarrës që janë larguar
      </h3>

      {inactiveTenants.length === 0 ? (
        <div
          style={{
            marginTop: 12,
            padding: "18px",
            textAlign: "center",
            background: "#f9f9f9",
            border: "1px solid #ddd",
            borderRadius: "10px",
          }}
        >
          Nuk ka qiramarrës të larguar.
        </div>
      ) : (
      <table className="tenant-table">
        <thead>
          <tr>
            <th>Emri</th>
            <th>Telefoni</th>
            <th>Apartamenti</th>
            <th>Parking</th>
            <th>Data pagesës</th>
            <th>Kontrata</th>
            <th>Statusi</th>
            <th>Veprime</th>
          </tr>
        </thead>

        <tbody>
          {inactiveTenants.map((tenant) => {
            const isEditingRow = editing === tenant.id;

            const parkings = normalizeParkings(
              tenant.parkings || []
            );

            const parkingNumbers = parkings
              .map((parking) =>
                String(
                  parking.numero_parcheggio || ""
                ).trim()
              )
              .filter(Boolean);

            const legacyParkingNumber = String(
              tenant.numero_parcheggio || ""
            ).trim();

            if (
              legacyParkingNumber &&
              !parkingNumbers.includes(
                legacyParkingNumber
              )
            ) {
              parkingNumbers.push(
                legacyParkingNumber
              );
            }

            const parkingSummary =
              parkingNumbers.length > 0
                ? parkingNumbers.join(", ")
                : "Jo";

            return (
              <tr key={tenant.id}>
                <td>
                  {isEditingRow ? (
                    <input
                      value={editForm.name}
                      onChange={(event) =>
                        setEditForm({
                          ...editForm,
                          name: event.target.value,
                        })
                      }
                    />
                  ) : (
                    tenant.name
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <input
                      placeholder="Telefoni (+355...)"
                      value={editForm.phone}
                      onChange={(event) =>
                        setEditForm({
                          ...editForm,
                          phone: event.target.value,
                        })
                      }
                    />
                  ) : (
                    <>
                      {visiblePhones[tenant.id]
                        ? `📞 ${tenant.phone || "-"}`
                        : maskPhone(tenant.phone)}

                      {tenant.phone ? (
                        <button
                          onClick={() =>
                            togglePhone(tenant.id)
                          }
                          title={
                            visiblePhones[tenant.id]
                              ? "Fshih numrin"
                              : "Shfaq numrin"
                          }
                          style={{
                            marginLeft: 10,
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            fontSize: 14,
                          }}
                        >
                          👁
                        </button>
                      ) : null}
                    </>
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <select
                      value={editForm.apartment_id}
                      onChange={(event) =>
                        setEditForm({
                          ...editForm,
                          apartment_id:
                            event.target.value,
                        })
                      }
                    >
                      <option value="">
                        Pa apartament (vetëm parking)
                      </option>

                      {apartments.map((apartment) => (
                        <option
                          key={apartment.id}
                          value={apartment.id}
                        >
                          {apartment.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    tenant.apartment_name || "-"
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <>
                      <label className="tenant-checkbox">
                        <input
                          type="checkbox"
                          checked={
                            (editForm.parkings || [])
                              .length > 0
                          }
                          onChange={(event) => {
                            if (event.target.checked) {
                              if (
                                (editForm.parkings || [])
                                  .length === 0
                              ) {
                                addParkingTo(
                                  editForm,
                                  setEditForm
                                );
                              }
                            } else {
                              setEditForm({
                                ...editForm,
                                parkings: [],
                              });
                            }
                          }}
                        />
                        Po
                      </label>

                      {(editForm.parkings || []).map(
                        (parking, index) => (
                          <div
                            key={index}
                            className="parking-row"
                          >
                            <input
                              placeholder="Numri"
                              value={
                                parking.numero_parcheggio
                              }
                              onChange={(event) =>
                                updateParkingField(
                                  index,
                                  "numero_parcheggio",
                                  event.target.value,
                                  editForm,
                                  setEditForm
                                )
                              }
                            />

                            <input
                              type="number"
                              placeholder="Qira"
                              value={
                                parking.affitto_parcheggio
                              }
                              onChange={(event) =>
                                updateParkingField(
                                  index,
                                  "affitto_parcheggio",
                                  event.target.value,
                                  editForm,
                                  setEditForm
                                )
                              }
                            />

                            <input
                              type="number"
                              placeholder="Shpenzime"
                              value={
                                parking.spese_parcheggio
                              }
                              onChange={(event) =>
                                updateParkingField(
                                  index,
                                  "spese_parcheggio",
                                  event.target.value,
                                  editForm,
                                  setEditForm
                                )
                              }
                            />

                            <button
                              type="button"
                              className="tenant-btn cancel small"
                              onClick={() =>
                                removeParkingFrom(
                                  index,
                                  editForm,
                                  setEditForm
                                )
                              }
                            >
                              Hiq
                            </button>
                          </div>
                        )
                      )}

                      {(editForm.parkings || []).length >
                        0 && (
                        <button
                          type="button"
                          className="tenant-btn"
                          onClick={() =>
                            addParkingTo(
                              editForm,
                              setEditForm
                            )
                          }
                        >
                          + Shto parking
                        </button>
                      )}
                    </>
                  ) : (
                    parkingSummary
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <select
                      value={editForm.payment_range}
                      onChange={(event) =>
                        setEditForm({
                          ...editForm,
                          payment_range:
                            event.target.value,
                        })
                      }
                    >
                      {PAYMENT_RANGES.map((range) => (
                        <option
                          key={range.value}
                          value={range.value}
                        >
                          {range.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    tenant.payment_range || "-"
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        minWidth: "150px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "12px",
                            marginBottom: "4px",
                            fontWeight: "600",
                          }}
                        >
                          Fillimi
                        </div>

                        <input
                          type="date"
                          title="Data e fillimit të kontratës"
                          value={editForm.start_date}
                          onChange={(event) =>
                            setEditForm({
                              ...editForm,
                              start_date:
                                event.target.value,
                            })
                          }
                        />
                      </div>

                      <div>
                        <div
                          style={{
                            fontSize: "12px",
                            marginBottom: "4px",
                            fontWeight: "600",
                          }}
                        >
                          Mbarimi
                        </div>

                        <input
                          type="date"
                          title="Data e mbarimit të kontratës"
                          value={editForm.end_date}
                          onChange={(event) =>
                            setEditForm({
                              ...editForm,
                              end_date:
                                event.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: "13px",
                        lineHeight: "1.6",
                      }}
                    >
                      <div>
                        <strong>Fillimi:</strong>{" "}
                        {tenant.start_date || "-"}
                      </div>

                      <div>
                        <strong>Mbarimi:</strong>{" "}
                        {tenant.end_date || "-"}
                      </div>
                    </div>
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <label className="tenant-checkbox">
                      <input
                        type="checkbox"
                        checked={editForm.is_active}
                        onChange={(event) =>
                          setEditForm({
                            ...editForm,
                            is_active:
                              event.target.checked,
                          })
                        }
                      />
                      Aktiv
                    </label>
                  ) : tenant.is_active !== false ? (
                    "Aktiv"
                  ) : (
                    "Jo aktiv"
                  )}
                </td>

                <td>
                  {isEditingRow ? (
                    <>
                      <button
                        className="tenant-btn"
                        onClick={() =>
                          updateTenant(tenant.id)
                        }
                      >
                        Ruaj
                      </button>

                      <button
                        className="tenant-btn cancel"
                        onClick={cancelEdit}
                      >
                        Anulo
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="edit-btn"
                        onClick={() =>
                          startEdit(tenant)
                        }
                      >
                        Modifiko
                      </button>

                      <button
                        className="tenant-btn"
                        onClick={() =>
                          renewTenantContract(tenant)
                        }
                      >
                        Rinovo kontratën
                      </button>

                      {tenant.is_active !== false ? (
                        <button
                          className="delete-btn"
                          onClick={() =>
                            changeTenantStatus(
                              tenant,
                              false
                            )
                          }
                        >
                          Çaktivizo
                        </button>
                      ) : (
                        <button
                          className="tenant-btn"
                          onClick={() =>
                            changeTenantStatus(
                              tenant,
                              true
                            )
                          }
                        >
                          Riaktivizo
                        </button>
                      )}

                      <button
                        className="delete-btn"
                        onClick={() =>
                          deleteTenant(tenant.id)
                        }
                      >
                        Fshi
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      )}
    </div>
  );
}
